import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { SlaRunStateIcon } from '@components/ui/sla-timer';
import { SimpleTooltipV2 } from '@components/ui/tooltip';
import { AlertCircle, AlertTriangle, ChevronDown, Link2, X } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/utils';
import { buildRoutePath } from '@/utils/routes';

import { formatCallAttempts, formatSla } from '../data/formatters';
import { canReassignCase } from '../data/reassign';
import type { Agent, CaseItem } from '../data/types';
import { copyCaseLink } from './copyCaseLink';
import { PayerHoursPopover } from './PayerHoursPopover';
import { ReassignPopover } from './ReassignPopover';

/**
 * Shared column template for every row (payer, program, disposition, missing
 * badge, AI / human call counts, risk icon, SLA) so the columns line up across
 * rows. Program takes the free width; the rest are fixed.
 */
const ROW_GRID = 'grid grid-cols-[9rem_minmax(0,1fr)_9rem_4.5rem_5.5rem_1rem_7.5rem]';

interface AggregateRowListProps {
  agentName: string;
  columnLabel: string;
  teamLabel?: string;
  cases: CaseItem[];
  reassignableAgents: Agent[];
  inFlight: boolean;
  /** Disabled-on-mutation flag from the underlying `useReassignCase`. */
  isReassigning: boolean;
  onPickReassign: (agentId: string) => void;
  onClose: () => void;
  onSelectionChange: (selectedIds: Set<string>) => void;
  selectedIds: Set<string>;
}

/**
 * The popover body for an `AggregateCell`: header (select-all + reassign
 * dropdown + close) and a scrollable row list with per-row checkbox and
 * open-case nav. Selection state is owned by the parent so it can be reset
 * on close.
 */
export function AggregateRowList({
  agentName,
  columnLabel,
  teamLabel,
  cases,
  reassignableAgents,
  inFlight,
  isReassigning,
  onPickReassign,
  onClose,
  onSelectionChange,
  selectedIds,
}: AggregateRowListProps) {
  const [reassignOpen, setReassignOpen] = useState(false);

  // A case on a live human call cannot be reassigned (LA-8061): its row has no
  // usable checkbox, and select-all and the count skip it. A selected case whose
  // call goes live while the popover is open drops out of the count the same way.
  const reassignableCases = cases.filter(canReassignCase);
  const selectedCount = reassignableCases.filter(c => selectedIds.has(c.id)).length;
  const allSelected = reassignableCases.length > 0 && selectedCount === reassignableCases.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const headerCheckedState: boolean | 'indeterminate' = allSelected
    ? true
    : someSelected
      ? 'indeterminate'
      : false;

  // Open case-details in a new tab so the user keeps the aggregate popover
  // (and its selection state) in the original tab. `noopener,noreferrer`
  // strips the window-opener reference and the `Referer` header.
  const openCase = (c: CaseItem) => {
    if (!c.id || !c.organizationId || !c.programId) return;
    const url = buildRoutePath(`/cases/${c.id}`, c.organizationId, c.programId);
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handleCopyLink = (c: CaseItem, e: React.MouseEvent) => {
    e.stopPropagation();
    copyCaseLink(c);
  };

  const toggleAll = () => {
    onSelectionChange(allSelected ? new Set() : new Set(reassignableCases.map(c => c.id)));
  };

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  return (
    <>
      <div className="flex items-center gap-3 p-3 border-b border-border">
        <Checkbox
          checked={headerCheckedState}
          onCheckedChange={toggleAll}
          disabled={reassignableCases.length === 0}
          aria-label="Select all cases"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-foreground truncate">{agentName}</div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground truncate">
            {teamLabel ? `${teamLabel} · ` : ''}
            {columnLabel} · {cases.length}
          </div>
        </div>
        {reassignableCases.length > 0 && (
          <ReassignPopover
            agents={reassignableAgents}
            open={reassignOpen}
            onOpenChange={setReassignOpen}
            onPick={agentId => {
              setReassignOpen(false);
              onPickReassign(agentId);
            }}
          >
            <Button
              variant="outline"
              size="sm"
              disabled={inFlight || isReassigning || selectedCount === 0}
              className="w-[170px] justify-between px-3 font-normal"
            >
              <span className={cn('truncate', selectedCount === 0 && 'text-muted-foreground')}>
                {inFlight
                  ? 'Reassigning…'
                  : selectedCount > 0
                    ? `Reassign ${selectedCount}`
                    : 'Reassign'}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </ReassignPopover>
        )}
        <Button variant="ghost" size="icon" className="size-7" onClick={onClose} aria-label="Close">
          <X className="size-4" />
        </Button>
      </div>
      <div className="max-h-[420px] overflow-auto">
        {cases.map(c => {
          const canReassign = canReassignCase(c);
          const isSelected = canReassign && selectedIds.has(c.id);
          const slaPositive = c.slaDeltaMinutes >= 0;
          const riskTone =
            c.riskLevel === 'breached'
              ? 'text-critical-foreground'
              : c.riskLevel === 'warning'
                ? 'text-attention-foreground'
                : '';
          const canOpen = !!c.id && !!c.organizationId && !!c.programId;
          return (
            <div
              key={c.id}
              className={cn(
                'group flex items-center gap-3 px-3 py-2 border-b border-border last:border-b-0',
                'hover:bg-accent/30 transition-colors',
                isSelected && 'bg-accent/40',
              )}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleOne(c.id)}
                disabled={!canReassign}
                aria-label={`Select ${c.payer} ${c.program} case`}
              />
              <div
                role={canOpen ? 'link' : undefined}
                tabIndex={canOpen ? 0 : undefined}
                onClick={canOpen ? () => openCase(c) : undefined}
                onKeyDown={
                  canOpen
                    ? e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openCase(c);
                        }
                      }
                    : undefined
                }
                aria-label={canOpen ? `Open case ${c.payer} ${c.program}` : undefined}
                className={cn(
                  'flex-1 min-w-0 items-center gap-2',
                  ROW_GRID,
                  canOpen &&
                    'cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                {/* Every column is a fixed slot, rendered even when empty, so
                    each field starts at the same x on every row. */}
                <span
                  className="min-w-0 truncate text-sm font-semibold text-foreground"
                  data-slot="payer"
                  title={c.payer}
                >
                  {c.payer}
                </span>
                <span
                  className="min-w-0 flex items-baseline gap-1 text-sm"
                  data-slot="program"
                  title={[c.program, c.orgName].filter(Boolean).join(' · ') || undefined}
                >
                  {c.program && (
                    <span className="min-w-0 font-medium text-foreground/90 truncate">
                      {c.program}
                    </span>
                  )}
                  {c.orgName && (
                    <span className="min-w-0 text-xs italic text-muted-foreground truncate">
                      {c.orgName}
                    </span>
                  )}
                </span>
                <span className="min-w-0 flex" data-slot="disposition">
                  {c.dispositionLabel && (
                    <Badge
                      variant="outline"
                      className="min-w-0 max-w-full px-1.5 py-0 text-[10px] font-medium"
                      title={c.dispositionLabel}
                    >
                      <span className="truncate">{c.dispositionLabel}</span>
                    </Badge>
                  )}
                </span>
                <span className="min-w-0 flex" data-slot="missing">
                  {(c.missingFieldsCount ?? 0) > 0 && (
                    <Badge variant="warning" className="px-1.5 py-0 text-[10px] shrink-0">
                      {c.missingFieldsCount} missing
                    </Badge>
                  )}
                </span>
                {/* Stacked so both counts fit one narrow slot. */}
                <span className="min-w-0 flex flex-col items-start gap-0.5" data-slot="calls">
                  {(c.aiCallAttempts ?? 0) > 0 && (
                    <Badge variant="outline" className="px-1.5 py-0 text-[10px] whitespace-nowrap">
                      {formatCallAttempts(c.aiCallAttempts ?? 0, 'AI')}
                    </Badge>
                  )}
                  {(c.humanCallAttempts ?? 0) > 0 && (
                    <Badge variant="outline" className="px-1.5 py-0 text-[10px] whitespace-nowrap">
                      {formatCallAttempts(c.humanCallAttempts ?? 0, 'human')}
                    </Badge>
                  )}
                </span>
                <span className="flex" data-slot="risk">
                  {/* Match CaseCard: `AlertCircle` for breached, `AlertTriangle`
                      for at-risk. Different shapes — not just colour — so the
                      signal survives without colour perception. */}
                  {c.riskLevel === 'breached' && (
                    <AlertCircle
                      className={cn('size-3.5 shrink-0', riskTone)}
                      aria-label="SLA breached"
                    />
                  )}
                  {c.riskLevel === 'warning' && (
                    <AlertTriangle
                      className={cn('size-3.5 shrink-0', riskTone)}
                      aria-label="At risk of SLA breach"
                    />
                  )}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center justify-end gap-1 text-[10px] uppercase tracking-wider tabular-nums whitespace-nowrap font-medium',
                    slaPositive ? 'text-positive-foreground' : 'text-critical-foreground',
                  )}
                >
                  {/* Same paused / running signal as CaseCard, in its muted tone. */}
                  <SlaRunStateIcon paused={c.slaPaused} className="text-muted-foreground" />
                  <span className="text-muted-foreground/70 font-normal">SLA</span>{' '}
                  {formatSla(c.slaDeltaMinutes)}
                </span>
              </div>
              {/* Always rendered at a fixed width so rows never shift on hover
                  or when a row cannot be opened. */}
              <div className="flex w-[3.25rem] shrink-0 items-center gap-1" data-slot="actions">
                {canOpen && <PayerHoursPopover caseItem={c} />}
                {canOpen && (
                  <SimpleTooltipV2 content="Copy case link">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Copy case link"
                      className={cn(
                        'size-6 shrink-0 transition-opacity',
                        'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                      )}
                      onClick={e => handleCopyLink(c, e)}
                    >
                      <Link2 className="size-3.5" />
                    </Button>
                  </SimpleTooltipV2>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
